import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { OrderPosition } from '../shared/OrderPosition';

interface ReceiptArticle {
  article: {
    name: string;
    ptu: string;
    price: string;
    quantity: string;
    value: string;
  };
}

// PTU (stawka VAT -> litera) zgodnie z konfiguracją liter na drukarce fiskalnej.
const PTU_MAP: Record<string, string> = {
  '23.00': 'A',
  '8.00': 'B',
  '5.00': 'C',
  '0.00': 'D',
};

/**
 * Integracja z lokalnym REST API drukarki fiskalnej Novitus (Online API).
 *
 * Drukarka jest dostępna tylko w sieci lokalnej stanowiska (http, bez
 * certyfikatu), a strona aplikacji działa na https - bezpośrednie wywołanie
 * z przeglądarki byłoby "mixed content" i zostałoby zablokowane albo
 * po cichu podbite na https (które i tak by się wywaliło, bo drukarka nie
 * ma serwera https). Dlatego zapytania idą przez lokalnego agenta
 * ("agent drukarki fiskalnej" w repo) nasłuchującego na 127.0.0.1 - to jest
 * adres jawnie wyłączony z reguł mixed content przez przeglądarki, więc
 * wywołanie zawsze przechodzi. Agent, działający jako osobny proces (poza
 * przeglądarką), przekazuje żądanie dalej do prawdziwego IP drukarki.
 *
 * Jeśli błąd sieciowy pojawia się od razu po kliknięciu (bez żadnej
 * odpowiedzi), pierwsze podejrzenie to brak uruchomionego agenta na tym
 * stanowisku (patrz README w folderze agenta).
 */
@Injectable({
  providedIn: 'root',
})
export class FiscalPrinterService {
  private readonly AGENT_BASE_URL = 'http://127.0.0.1:9977';
  private readonly IP_KEY = 'fiscalPrinterIp';
  private readonly TOKEN_KEY = 'fiscalPrinterToken';
  private readonly TOKEN_EXP_KEY = 'fiscalPrinterTokenExpiration';
  private readonly REACHABLE_KEY = 'fiscalPrinterReachable';
  private readonly REACHABLE_CHECKED_AT_KEY = 'fiscalPrinterReachableCheckedAt';
  private readonly REACHABLE_TTL_MS = 24 * 60 * 60 * 1000;
  private readonly REACHABLE_CHECK_TIMEOUT_MS = 1500;

  private reachabilityCheckInFlight: Promise<boolean> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Sprawdza, czy to stanowisko widzi drukarkę fiskalną w swojej sieci lokalnej.
   * Wynik jest cache'owany w localStorage na 24h, żeby nie odpytywać przy
   * każdym renderze listy zamówień (order-menu jest instancjonowany per wiersz).
   */
  async isPrinterReachable(): Promise<boolean> {
    const cached = this.getCachedReachability();
    if (cached !== null) {
      return cached;
    }

    if (!this.reachabilityCheckInFlight) {
      this.reachabilityCheckInFlight = this.checkReachabilityNow().finally(() => {
        this.reachabilityCheckInFlight = null;
      });
    }

    return this.reachabilityCheckInFlight;
  }

  private getCachedReachability(): boolean | null {
    const checkedAtRaw = localStorage.getItem(this.REACHABLE_CHECKED_AT_KEY);
    if (!checkedAtRaw) {
      return null;
    }

    const checkedAt = Number(checkedAtRaw);
    if (!checkedAt || Date.now() - checkedAt > this.REACHABLE_TTL_MS) {
      return null;
    }

    return localStorage.getItem(this.REACHABLE_KEY) === '1';
  }

  private async checkReachabilityNow(): Promise<boolean> {
    const ip = localStorage.getItem(this.IP_KEY);

    if (!ip) {
      this.storeReachability(false);
      return false;
    }

    const baseUrl = this.buildBaseUrl(ip);

    try {
      await this.http
        .get(baseUrl, { headers: this.headers() })
        .pipe(timeout(this.REACHABLE_CHECK_TIMEOUT_MS))
        .toPromise();
      this.storeReachability(true);
      return true;
    } catch {
      this.storeReachability(false);
      return false;
    }
  }

  private storeReachability(reachable: boolean): void {
    localStorage.setItem(this.REACHABLE_KEY, reachable ? '1' : '0');
    localStorage.setItem(this.REACHABLE_CHECKED_AT_KEY, String(Date.now()));
  }

  private clearReachabilityCache(): void {
    localStorage.removeItem(this.REACHABLE_KEY);
    localStorage.removeItem(this.REACHABLE_CHECKED_AT_KEY);
  }

  async printReceipt(positions: OrderPosition[]): Promise<void> {
    if (!positions || positions.length === 0) {
      throw new Error('Brak pozycji do wydrukowania na paragonie.');
    }

    const baseUrl = this.getBaseUrl();

    await this.checkConnection(baseUrl);
    const token = await this.ensureValidToken(baseUrl);

    const items = this.buildReceiptItems(positions);
    const total = items
      .reduce((sum, item) => sum + Number(item.article.value), 0)
      .toFixed(2);

    const receiptId = await this.printReceiptRequest(baseUrl, token, items, total);
    await this.confirmReceiptRequest(baseUrl, token, receiptId);
  }

  changePrinterIp(): void {
    const current = localStorage.getItem(this.IP_KEY) ?? '';
    const ip = window.prompt(
      'Podaj adres IP drukarki fiskalnej w sieci lokalnej (np. 192.168.1.208):',
      current,
    );

    if (ip && ip.trim().length > 0) {
      localStorage.setItem(this.IP_KEY, this.sanitizeIp(ip));
      this.clearReachabilityCache();
    }
  }

  // =============== KONFIGURACJA ===============

  /**
   * Usuwa z wpisanego adresu ewentualny protokół (http://), port i ścieżkę,
   * na wypadek gdyby ktoś wkleił cały URL zamiast samego IP - inaczej
   * doklejenie własnego ":8888/api/v1" tworzyłoby zepsuty adres. Usuwa też
   * zera wiodące w oktetach (np. "192.168.000.120" -> "192.168.0.120") -
   * niektóre drukarki tak pokazują/eksportują swoje IP, a Node po stronie
   * agenta nie rozpoznaje takiej postaci jako adresu IP (próbuje ją
   * rozwiązać jako nazwę hosta i dostaje ENOTFOUND).
   */
  private sanitizeIp(raw: string): string {
    const cleaned = raw
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '');

    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(cleaned)) {
      return cleaned.split('.').map((part) => String(Number(part))).join('.');
    }

    return cleaned;
  }

  private getBaseUrl(): string {
    let ip = localStorage.getItem(this.IP_KEY);

    if (!ip) {
      ip = window.prompt(
        'Podaj adres IP drukarki fiskalnej w sieci lokalnej (np. 192.168.1.208):',
      );

      if (!ip || ip.trim().length === 0) {
        throw new Error('Nie podano adresu IP drukarki fiskalnej.');
      }

      ip = this.sanitizeIp(ip);
      localStorage.setItem(this.IP_KEY, ip);
    } else {
      ip = this.sanitizeIp(ip);
    }

    return this.buildBaseUrl(ip);
  }

  /**
   * Adres API drukarki przekazany przez lokalnego agenta zamiast wywoływany
   * bezpośrednio - patrz komentarz nad klasą. Agent nie zna IP drukarki na
   * stałe, dostaje je jako część ścieżki przy każdym zapytaniu.
   */
  private buildBaseUrl(ip: string): string {
    return `${this.AGENT_BASE_URL}/relay/${this.sanitizeIp(ip)}/api/v1`;
  }

  private headers(token?: string): HttpHeaders {
    let headers = new HttpHeaders({ Accept: 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  // =============== POŁĄCZENIE / TOKEN ===============

  private async checkConnection(baseUrl: string): Promise<void> {
    try {
      await this.http.get(baseUrl, { headers: this.headers() }).toPromise();
    } catch (error) {
      throw new Error(
        'Nie można połączyć się z drukarką fiskalną. Sprawdź, czy: 1) agent drukarki fiskalnej jest ' +
          'uruchomiony na tym stanowisku (http://127.0.0.1:9977/health powinno odpowiadać), ' +
          '2) drukarka jest włączona i w tej samej sieci lokalnej co to stanowisko.',
      );
    }
  }

  private async ensureValidToken(baseUrl: string): Promise<string> {
    const storedToken = localStorage.getItem(this.TOKEN_KEY);

    if (storedToken && (await this.isTokenValid(baseUrl, storedToken))) {
      return storedToken;
    }

    if (storedToken) {
      const refreshedToken = await this.tryRefreshToken(baseUrl, storedToken);
      if (refreshedToken) {
        return refreshedToken;
      }
    }

    return this.fetchNewToken(baseUrl);
  }

  private async isTokenValid(baseUrl: string, token: string): Promise<boolean> {
    try {
      await this.http
        .get(`${baseUrl}/queue`, { headers: this.headers(token) })
        .toPromise();
      return true;
    } catch {
      return false;
    }
  }

  private async tryRefreshToken(baseUrl: string, token: string): Promise<string | null> {
    try {
      const response: any = await this.http
        .patch(`${baseUrl}/token`, null, { headers: this.headers(token) })
        .toPromise();
      this.storeToken(response.token, response.expiration_date);
      return response.token;
    } catch {
      return null;
    }
  }

  private async fetchNewToken(baseUrl: string): Promise<string> {
    try {
      const response: any = await this.http
        .get(`${baseUrl}/token`, { headers: this.headers() })
        .toPromise();
      this.storeToken(response.token, response.expiration_date);
      return response.token;
    } catch (error: any) {
      if (error?.status === 429) {
        throw new Error(
          'Zbyt wiele zapytań o token do drukarki fiskalnej. Spróbuj ponownie za chwilę.',
        );
      }
      throw new Error('Nie udało się uzyskać tokenu z drukarki fiskalnej.');
    }
  }

  private storeToken(token: string, expirationDate: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXP_KEY, expirationDate ?? '');
  }

  // =============== PARAGON ===============

  private buildReceiptItems(positions: OrderPosition[]): ReceiptArticle[] {
    return positions.map((position) => {
      const price = Number(position.finalPriceBrutto ?? 0);
      const quantity = Number(position.quantity ?? 0);
      const value = Math.round(price * quantity * 100) / 100;
      const ptu = this.getPtu(position.tax);
      const name = this.buildArticleName(position.name ?? '', ptu);

      return {
        article: {
          name,
          ptu,
          price: price.toFixed(2),
          quantity: quantity.toFixed(2),
          value: value.toFixed(2),
        },
      };
    });
  }

  /**
   * Drukarka fiskalna wiąże nazwę towaru (PLU) na stałe z jedną stawką VAT -
   * ten sam produkt sprzedany kiedyś z inną stawką zostaje odrzucony przy
   * druku. Dopisanie litery PTU do nazwy rozdziela je na osobne pozycje w
   * bazie drukarki, więc każda stawka ma swój własny wpis.
   */
  private buildArticleName(rawName: string, ptu: string): string {
    const suffix = ` (${ptu})`;
    const maxNameLength = 60 - suffix.length;
    return rawName.substring(0, maxNameLength) + suffix;
  }

  private getPtu(tax: number): string {
    const key = Number(tax ?? 0).toFixed(2);
    const ptu = PTU_MAP[key];

    if (!ptu) {
      throw new Error(`Brak zdefiniowanej litery PTU dla stawki VAT ${tax}%.`);
    }

    return ptu;
  }

  private async printReceiptRequest(
    baseUrl: string,
    token: string,
    items: ReceiptArticle[],
    total: string,
  ): Promise<number> {
    const payload = {
      receipt: {
        items,
        summary: {
          total,
          pay_in: total,
        },
      },
    };

    try {
      const response: any = await this.http
        .post(`${baseUrl}/receipt`, payload, { headers: this.headers(token) })
        .toPromise();
      return response.request.id;
    } catch (error: any) {
      const description = error?.error?.exception?.description;
      throw new Error(
        description
          ? `Błąd druku paragonu: ${description}`
          : 'Błąd druku paragonu na drukarce fiskalnej.',
      );
    }
  }

  private async confirmReceiptRequest(
    baseUrl: string,
    token: string,
    receiptId: number,
  ): Promise<void> {
    try {
      await this.http
        .put(
          `${baseUrl}/receipt/${receiptId}`,
          { id: receiptId },
          { headers: this.headers(token) },
        )
        .toPromise();
    } catch (error) {
      throw new Error(
        'Paragon został wysłany do wydruku, ale nie udało się potwierdzić wydruku na drukarce.',
      );
    }
  }
}
